/**
 * The error surface, typed off the status codes the module's routers actually produce.
 *
 * `core/api/routing.py::IdempotentDomainRoute` translates store exceptions to HTTP:
 *
 *   404  NotFound            — absent, or another tenant's row (the two are deliberately
 *                              indistinguishable; the distinction is itself a leak)
 *   409  Conflict            — a uniqueness or business rule refused the write. Includes
 *                              `InsufficientStock` and `ReservationRefused`, which are
 *                              distinct on the server and are told apart here too
 *   422  ValidationFailed    — the payload is internally inconsistent, or the same
 *                              idempotency key arrived with a different body
 *   503  StoreUnavailable    — remote mode could not reach the canonical service
 *
 * and `core/api/idempotency.py` adds:
 *
 *   400  the Idempotency-Key header is missing
 *   409  an identical request is already in flight
 *
 * Retry policy hangs off these classes. A 409 that is `IdempotencyInFlightError` is worth
 * retrying; a 409 that is `InsufficientStockError` is not, and retrying it is how a
 * picker ends up sent back to the same empty shelf.
 */
export class InventoryApiError extends Error {
    status;
    detail;
    requestId;
    constructor(status, detail, message, requestId) {
        super(message);
        this.name = 'InventoryApiError';
        this.status = status;
        this.detail = detail;
        this.requestId = requestId;
    }
}
/** No verified tenant claim reached the module. The host's session has lapsed. */
export class UnauthorizedError extends InventoryApiError {
    name = 'UnauthorizedError';
}
/**
 * The tenant is known and the plan does not include this capability.
 *
 * Invariant 7: this is the enforcement. A skin hiding the button is a courtesy, and a
 * caller must still be prepared for the 403 — a plan can change between the entitlement
 * query and the click.
 */
/**
 * A 403 that is NOT about the plan.
 *
 * The server raises 403 for two unrelated reasons: the tenant's plan lacks a feature, and
 * the token names no organization this module can serve (`tenant_claim_missing`,
 * `tenant_claim_unusable`). This client mapped BOTH onto FeatureNotInPlanError, so a
 * caller whose token was not bound to a tenant was told to upgrade their subscription.
 * Found by the standalone shell, where an unbound token is a routine first-run state.
 */
export class ForbiddenError extends InventoryApiError {
    name = 'ForbiddenError';
}
export class FeatureNotInPlanError extends ForbiddenError {
    name = 'FeatureNotInPlanError';
    feature;
    constructor(status, detail, message, feature, requestId) {
        super(status, detail, message, requestId);
        this.feature = feature;
    }
}
export class NotFoundError extends InventoryApiError {
    name = 'NotFoundError';
}
export class ConflictError extends InventoryApiError {
    name = 'ConflictError';
}
/** Outbound movement refused: the warehouse forbids a negative balance. */
export class InsufficientStockError extends ConflictError {
    name = 'InsufficientStockError';
}
/**
 * Outbound movement refused: the stock exists but is hard-reserved for something else.
 *
 * Distinct from `InsufficientStockError` on purpose, on both sides of the wire. Telling a
 * picker there is no stock when it is sitting in front of them, promised to another order,
 * sends them on a hunt that ends in a manual adjustment.
 */
export class ReservationRefusedError extends ConflictError {
    name = 'ReservationRefusedError';
}
/** An identical request with this key is still running. Safe to retry with the SAME key. */
export class IdempotencyInFlightError extends ConflictError {
    name = 'IdempotencyInFlightError';
}
/**
 * The same idempotency key arrived with a different body. Always a caller bug — a key was
 * reused across two different logical operations. Never retried automatically.
 */
export class IdempotencyMismatchError extends InventoryApiError {
    name = 'IdempotencyMismatchError';
}
/** The request never carried an `Idempotency-Key`. Also always a caller bug. */
export class IdempotencyKeyRequiredError extends InventoryApiError {
    name = 'IdempotencyKeyRequiredError';
}
export class ValidationError extends InventoryApiError {
    name = 'ValidationError';
    issues;
    constructor(status, detail, message, issues, requestId) {
        super(status, detail, message, requestId);
        this.issues = issues;
    }
}
/** Remote mode could not reach the canonical inventory service. Retryable. */
export class ServiceUnavailableError extends InventoryApiError {
    name = 'ServiceUnavailableError';
}
/**
 * The payload failed this package's own schema before it was sent.
 *
 * This is the "a form fails before a round trip" case. The server would have refused it
 * too — these schemas mirror the Pydantic constraints — but refusing here costs nothing
 * and keeps a warehouse operator on a flaky connection from waiting for a 422.
 */
export class RequestValidationError extends Error {
    operationName;
    issues;
    name = 'RequestValidationError';
    constructor(operationName, issues) {
        super(`${operationName} payload is not valid: ` +
            issues.map((i) => (i.path ? `${i.path}: ${i.message}` : i.message)).join('; '));
        this.operationName = operationName;
        this.issues = issues;
    }
}
/** The response did not match the schema this client version expects. */
export class ResponseShapeError extends Error {
    path;
    issues;
    raw;
    name = 'ResponseShapeError';
    constructor(path, issues, raw) {
        super(`response from ${path} did not match the expected shape: ` +
            issues.map((i) => `${i.path}: ${i.message}`).join('; '));
        this.path = path;
        this.issues = issues;
        this.raw = raw;
    }
}
/** The request never reached the server, or was aborted. Distinct from any HTTP status. */
export class NetworkError extends Error {
    path;
    cause;
    name = 'NetworkError';
    constructor(path, cause) {
        super(`request to ${path} failed before a response was received`);
        this.path = path;
        this.cause = cause;
    }
}
function detailText(detail) {
    if (typeof detail === 'string')
        return detail;
    if (detail && typeof detail === 'object') {
        const message = detail.message;
        if (typeof message === 'string')
            return message;
    }
    return null;
}
/**
 * The server's stable, machine-readable classification of the failure.
 *
 * This is what discrimination should key on. Matching on message text - which this client
 * had to do before the server carried a code - couples every consumer to our wording, so
 * rephrasing an error for clarity would silently reclassify failures here.
 */
function detailCode(detail) {
    if (detail && typeof detail === 'object') {
        const code = detail.code;
        if (typeof code === 'string')
            return code;
    }
    return null;
}
function validationIssues(detail) {
    if (!Array.isArray(detail))
        return [];
    return detail.map((raw) => {
        const item = raw;
        const loc = Array.isArray(item.loc) ? item.loc : [];
        // FastAPI prefixes the location with "body" / "query"; the caller cares about the field.
        const path = loc.slice(1).join('.') || loc.join('.');
        return { path, message: item.msg ?? 'invalid value' };
    });
}
/**
 * Turn a non-2xx response body into the most specific error class that fits.
 *
 * The 409 discrimination is done on the message text because the server sends a plain
 * string detail for store conflicts. That is fragile by nature, so every branch falls
 * back to the general `ConflictError` rather than mislabelling — a caller matching on
 * `ConflictError` keeps working if the wording changes.
 */
export function toApiError(status, detail, path, requestId) {
    const text = detailText(detail);
    const message = text ?? `${path} failed with HTTP ${status}`;
    const lowered = (text ?? '').toLowerCase();
    const code = detailCode(detail);
    switch (status) {
        case 400:
            if (code === 'idempotency_key_required' || (!code && lowered.includes('idempotency-key'))) {
                return new IdempotencyKeyRequiredError(status, detail, message, requestId);
            }
            return new InventoryApiError(status, detail, message, requestId);
        case 401:
            return new UnauthorizedError(status, detail, message, requestId);
        case 403: {
            const feature = detail && typeof detail === 'object'
                ? detail.feature
                : undefined;
            // A plan problem only when the server says so, or (older servers) when it named a
            // feature. Everything else 403 is a general refusal — a tenant claim the module
            // cannot use is not something a subscription upgrade fixes.
            if (code === 'feature_not_in_plan' || (!code && feature)) {
                return new FeatureNotInPlanError(status, detail, message, feature, requestId);
            }
            return new ForbiddenError(status, detail, message, requestId);
        }
        case 404:
            return new NotFoundError(status, detail, message, requestId);
        case 409:
            if (code === 'idempotency_in_flight' || (!code && lowered.includes('already in progress'))) {
                return new IdempotencyInFlightError(status, detail, message, requestId);
            }
            if (code === 'reservation_refused' || (!code && lowered.includes('reserv'))) {
                return new ReservationRefusedError(status, detail, message, requestId);
            }
            if (code === 'insufficient_stock' ||
                (!code && (lowered.includes('insufficient') || lowered.includes('negative stock')))) {
                return new InsufficientStockError(status, detail, message, requestId);
            }
            return new ConflictError(status, detail, message, requestId);
        case 422: {
            if (code === 'idempotency_mismatch' ||
                (!code && (lowered.includes('idempotency') || lowered.includes('different request')))) {
                return new IdempotencyMismatchError(status, detail, message, requestId);
            }
            const issues = validationIssues(detail);
            return new ValidationError(status, detail, issues.length > 0 ? issues.map((i) => `${i.path}: ${i.message}`).join('; ') : message, issues, requestId);
        }
        case 503:
            return new ServiceUnavailableError(status, detail, message, requestId);
        default:
            return new InventoryApiError(status, detail, message, requestId);
    }
}
/**
 * Whether retrying is safe AND useful.
 *
 * Retrying is only ever safe because every mutating request carries an idempotency key
 * that the caller reuses across attempts — see `http/idempotency.ts`. Without that this
 * function would have to return false for every write.
 */
export function isRetryable(error) {
    if (error instanceof NetworkError)
        return true;
    if (error instanceof IdempotencyInFlightError)
        return true;
    if (error instanceof ServiceUnavailableError)
        return true;
    if (error instanceof InventoryApiError)
        return error.status >= 500;
    return false;
}
/**
 * The server's stable classification of a failure, for a caller that needs to branch.
 *
 * `toApiError` reads this internally to pick an error class, and exposing it means a skin
 * that needs a distinction the class hierarchy does not draw — an unconfigured service
 * versus an ordinary sign-in, say — does not have to re-parse the body. The standalone
 * shell wrote its own copy of these six lines before this existed.
 */
export function errorCode(error) {
    if (error instanceof InventoryApiError)
        return detailCode(error.detail);
    return null;
}
/** The message a skin should put in front of a person. Never the raw JSON. */
export function getErrorMessage(error) {
    if (error instanceof RequestValidationError && error.issues.length > 0) {
        return error.issues.map((i) => (i.path ? `${i.path}: ${i.message}` : i.message)).join('\n');
    }
    if (error instanceof ValidationError && error.issues.length > 0) {
        return error.issues.map((i) => (i.path ? `${i.path}: ${i.message}` : i.message)).join('\n');
    }
    if (error instanceof FeatureNotInPlanError && error.feature) {
        return `Your plan does not include ${error.feature.replace(/_/g, ' ')}.`;
    }
    // Deliberately no fallback sentence for a featureless FeatureNotInPlanError: without a
    // feature to name, the server's own message says more than a guess about billing does.
    if (error instanceof InventoryApiError || error instanceof Error)
        return error.message;
    return 'An unexpected error occurred';
}
//# sourceMappingURL=errors.js.map